import React, { useEffect, useState } from "react";
import { fetchUserBadges } from "../../utils/utils";
import { FiCompass, FiEdit2, FiMessageSquare, FiPhone, FiMail } from "react-icons/fi";
import { MdVerifiedUser } from "react-icons/md";
import { FaHandHoldingHeart, FaPeopleCarry } from "react-icons/fa";
import {
  TIER,
  getBadgeCountLabel,
  processBadges,
} from "../../utils/badgeUtils";

function getIcon(series) {
  if (!series) return <FiCompass className="text-[20px]" />;
  const key = typeof series === "string" ? series.trim().toLowerCase() : "";
  switch (key) {
    case "civic_explorer": return <FiCompass className="text-[20px]" />;
    case "letter_writer": return <FiEdit2 className="text-[20px]" />;
    case "civic_caller": return <FiPhone className="text-[20px]" />;
    case "constituent_messenger": return <FiMail className="text-[20px]" />;
    case "verified_account": return <MdVerifiedUser className="text-[20px]" />;
    case "community_participant": return <FaPeopleCarry className="text-[20px]" />;
    case "community_volunteer": return <FaHandHoldingHeart className="text-[20px]" />;
    case "constituent_commentator": return <FiMessageSquare className="text-[20px]" />;
    default: return <FiCompass className="text-[20px]" />;
  }
}

export default function UserBadges({ apiBase = "" }) {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchUserBadges(apiBase);
        const filtered = (Array.isArray(data) ? data : []).filter(
          (b) => b.series !== "letter_writer"
        );
        setBadges(filtered);
      } catch (e) {
        setErr(e.message || "Failed to load badges");
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase]);

  if (loading) return <p className="text-sm text-slate-600">Loading badges…</p>;
  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!badges.length) return <p className="text-sm text-slate-600">No badges yet.</p>;

  const processedBadges = processBadges(badges);

  return (
    <section className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
      {processedBadges.map((badgeGroup) => {
        const { series, cleanName, highestEarned, nextTier, tiers } = badgeGroup;
        const earnedTierData = highestEarned ? tiers[highestEarned] : null;
        const t = highestEarned ? TIER[highestEarned] : TIER.bronze;
        const isGoldEarned = highestEarned === "gold";

        let pct = 0;
        let progressDescription = "";
        let progressFraction = null;

        if (isGoldEarned && earnedTierData) {
          progressDescription = getBadgeCountLabel(series, earnedTierData.current) || "Max tier reached";
        } else if (nextTier) {
          pct = Math.max(0, Math.min(100, Math.round((nextTier.current / nextTier.threshold) * 100)));
          progressDescription = nextTier.description || "Progress";
          progressFraction = `${nextTier.current}/${nextTier.threshold}`;
        }

        return (
          <div
            key={series}
            className="relative rounded-2xl border bg-white/90 p-3 sm:p-4 flex flex-col items-center text-center gap-2 shadow-sm hover:shadow-md transition group"
            style={{
              background: `linear-gradient(to bottom, ${t.tint}, #ffffff)`,
              boxShadow: `0 4px 12px rgba(0,0,0,.03), 0 2px 8px ${t.glow}`,
              opacity: highestEarned ? 1 : 0.9,
            }}
          >
            <div
              className="grid place-items-center w-12 h-12 rounded-xl border bg-white shadow-sm shrink-0 mb-1 transition-transform group-hover:scale-110"
              style={{ borderColor: t.color, color: t.color }}
              aria-hidden="true"
            >
              {getIcon(series)}
            </div>

            <div className="w-full min-w-0 flex flex-col items-center">
              <h4 className="font-bold text-slate-900 text-xs sm:text-sm leading-4 line-clamp-2 min-h-[2rem] flex items-center justify-center">
                {cleanName}
              </h4>

              {highestEarned && (
                <div className="mt-1.5">
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full border font-extrabold inline-block tracking-wide uppercase"
                    style={{
                      color: t.color,
                      borderColor: t.color,
                      background: `color-mix(in oklab, ${t.color} 10%, white 90%)`,
                    }}
                  >
                    {highestEarned}
                  </span>
                </div>
              )}

              {(nextTier || isGoldEarned) && (
                <div className="mt-3 w-full">
                  {!isGoldEarned && nextTier && (
                    <div className="h-1.5 w-full rounded-full bg-slate-200/80 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-teal-500 to-teal-600"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                  <div className="mt-1.5 flex justify-between gap-2 items-start w-full min-w-0">
                    <span
                      className="text-xs text-slate-900 font-medium min-w-0 break-words text-left"
                      title={progressDescription}
                    >
                      {progressDescription}
                    </span>
                    {progressFraction && (
                      <span className="shrink-0 text-xs text-slate-900 font-medium whitespace-nowrap">
                        {progressFraction}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
